import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useApi } from '../context/ApiProvider';
import LoadingScreen from '../components/LoadingScreen';
import ArtistCard from '../components/ArtistCard';
import SocialLinks from '../components/SocialLinks';
import ShowMore from '../components/ShowMore';
import { buildDemoOutreachMessage } from '../utils/demoOutreachMessage';

const STATUS_OPTIONS = ['candidate', 'demo_created', 'contacted', 'declined'];

const STATUS_COLORS = {
  candidate: 'default',
  demo_created: 'success',
  contacted: 'info',
  declined: 'warning',
};

function toDatetimeLocalValue(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function DemoCandidate() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [candidate, setCandidate] = useState(null);
  const [artist, setArtist] = useState(null);
  const [affiliatedLabels, setAffiliatedLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [artistLoading, setArtistLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [demoResult, setDemoResult] = useState(null);

  const [status, setStatus] = useState('candidate');
  const [outreachNotes, setOutreachNotes] = useState('');
  const [lastOutreachDt, setLastOutreachDt] = useState('');

  const handleApiError = useCallback(
    (response, fallback) => {
      if (response.status === 403) {
        enqueueSnackbar('Admin access required', { variant: 'error' });
      } else {
        enqueueSnackbar(
          response.body?.description || response.body?.message || fallback,
          { variant: 'error' }
        );
      }
    },
    [enqueueSnackbar]
  );

  const applyCandidate = useCallback((row) => {
    setCandidate(row);
    setStatus(row.status || 'candidate');
    setOutreachNotes(row.outreach_notes || '');
    setLastOutreachDt(toDatetimeLocalValue(row.last_outreach_dt));
  }, []);

  const loadArtist = useCallback(
    async (artistMetaId) => {
      setArtistLoading(true);
      const [artistRes, labelsRes] = await Promise.all([
        api.get(`/artists/${artistMetaId}`),
        api.get(`/artists/${artistMetaId}/labels`, {
          limit: 12,
          order_by: 'label_meta.name',
          order_direction: 'asc',
        }),
      ]);
      if (artistRes.ok) {
        setArtist(artistRes.body);
      } else {
        setArtist(null);
      }
      if (labelsRes.ok && Array.isArray(labelsRes.body?.data)) {
        setAffiliatedLabels(labelsRes.body.data);
      } else {
        setAffiliatedLabels([]);
      }
      setArtistLoading(false);
    },
    [api]
  );

  const loadCandidateFromList = useCallback(async () => {
    const listRes = await api.get('/demo/candidates');
    if (!listRes.ok) {
      return listRes;
    }
    const rows = Array.isArray(listRes.body) ? listRes.body : [];
    const row = rows.find((c) => c.id === Number(id));
    if (!row) {
      return { ok: false, status: 404, body: null };
    }
    return { ok: true, status: 200, body: row };
  }, [api, id]);

  const loadCandidate = useCallback(async () => {
    setLoading(true);
    let response = await api.get(`/demo/candidates/${id}`);
    if (!response.ok && response.status !== 403) {
      response = await loadCandidateFromList();
    }
    if (response.ok) {
      applyCandidate(response.body);
      if (response.body.artist_meta_id) {
        await loadArtist(response.body.artist_meta_id);
      }
    } else if (response.status === 404) {
      enqueueSnackbar('Candidate not found', { variant: 'error' });
      navigate('/admin/demo');
    } else {
      handleApiError(response, 'Failed to load candidate');
    }
    setLoading(false);
  }, [
    api,
    id,
    navigate,
    enqueueSnackbar,
    handleApiError,
    applyCandidate,
    loadCandidateFromList,
    loadArtist,
  ]);

  useEffect(() => {
    loadCandidate();
  }, [loadCandidate]);

  const outreachMessage = useMemo(
    () => buildDemoOutreachMessage({ candidate, demoResult }),
    [candidate, demoResult]
  );

  const handleSave = async () => {
    setSaving(true);
    const body = {
      status,
      outreach_notes: outreachNotes || null,
      last_outreach_dt: fromDatetimeLocalValue(lastOutreachDt),
    };
    const response = await api.put(`/demo/candidates/${id}`, body);
    if (response.ok) {
      setCandidate(response.body);
      enqueueSnackbar('Saved', { variant: 'success' });
    } else {
      handleApiError(response, 'Failed to save');
    }
    setSaving(false);
  };

  const handleCreateDemo = async (dryRun) => {
    if (!candidate) return;
    setCreating(true);
    const response = await api.post('/demo/accounts', {
      artist_slug: candidate.artist_slug,
      set_track_id: candidate.set_tracks_id,
      dry_run: dryRun,
    });
    if (response.ok) {
      setDemoResult(response.body);
      enqueueSnackbar(
        dryRun ? 'Dry run complete (rolled back)' : 'Demo account ready',
        { variant: 'success' }
      );
      if (!dryRun) {
        await loadCandidate();
      }
    } else {
      handleApiError(response, 'Failed to create demo');
    }
    setCreating(false);
  };

  const handleCleanup = async () => {
    if (!candidate?.demo_user_id) return;
    if (
      !window.confirm(
        `Delete demo user id=${candidate.demo_user_id}? This cannot be undone.`
      )
    ) {
      return;
    }
    setCleaning(true);
    const response = await api.delete(
      `/demo/accounts/${candidate.demo_user_id}`
    );
    if (response.ok) {
      enqueueSnackbar('Demo user deleted', { variant: 'success' });
      setDemoResult(null);
      await loadCandidate();
    } else {
      handleApiError(response, 'Failed to delete demo user');
    }
    setCleaning(false);
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(outreachMessage);
      enqueueSnackbar('Outreach message copied', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to copy message', { variant: 'error' });
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!candidate) {
    return null;
  }

  const foundedLabels = artist?.founded_label || [];

  return (
    <Box sx={{ py: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/admin/demo"
        sx={{ mb: 2 }}
      >
        Back to candidates
      </Button>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Artist
            </Typography>
            {artistLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            )}
            {!artistLoading && artist && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <ArtistCard
                  item={artist}
                  showFollowOption={false}
                  showDetailedInfo
                />
                <Button
                  component={RouterLink}
                  to={`/artists/${artist.id}`}
                  variant="outlined"
                  size="small"
                  endIcon={<OpenInNewIcon />}
                  fullWidth
                >
                  View on KIP
                </Button>
                <SocialLinks data={artist} type="artist" />
                {artist.country && (
                  <Typography variant="body2" color="text.secondary">
                    Country: {artist.country}
                  </Typography>
                )}
                {artist.email_address && (
                  <Typography variant="body2" color="text.secondary">
                    Email:{' '}
                    <Link href={`mailto:${artist.email_address}`}>
                      {artist.email_address}
                    </Link>
                  </Typography>
                )}
                {foundedLabels.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Founded labels
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {foundedLabels.map((label) => (
                        <Chip
                          key={label.id}
                          label={label.name}
                          size="small"
                          component={RouterLink}
                          to={`/labels/${label.id}`}
                          clickable
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
                {affiliatedLabels.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Labels
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {affiliatedLabels.map((label) => (
                        <Chip
                          key={label.id}
                          label={label.name}
                          size="small"
                          variant="outlined"
                          component={RouterLink}
                          to={`/labels/${label.id}`}
                          clickable
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
                {artist.bio && (
                  <Box sx={{ color: 'text.primary' }}>
                    <ShowMore label="Bio" content={artist.bio} />
                  </Box>
                )}
              </Stack>
            )}
            {!artistLoading && !artist && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {candidate.artist_name} ({candidate.artist_slug})
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <Paper sx={{ p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                sx={{ mb: 2 }}
              >
                <Typography variant="h5" component="h1">
                  Demo outreach
                </Typography>
                <Chip
                  label={candidate.status}
                  color={STATUS_COLORS[candidate.status] || 'default'}
                  size="small"
                />
              </Stack>

              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Target track & set
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Track
                  </Typography>
                  <Typography variant="body1">
                    {candidate.track_title || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Released {formatDate(candidate.track_release_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Set
                  </Typography>
                  <Typography variant="body1">
                    {candidate.set_title || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Released {formatDate(candidate.set_release_date)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Days after track release
                  </Typography>
                  <Typography variant="body1">
                    {candidate.days_after_release ?? '—'}
                  </Typography>
                </Grid>
                {candidate.is_label_boss && (
                  <Grid item xs={12} sm={6}>
                    <Chip label="Label boss" size="small" color="secondary" />
                  </Grid>
                )}
              </Grid>

              {(candidate.share_url || candidate.demo_user_id) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={0.5}>
                    {candidate.share_url && (
                      <Typography variant="body2">
                        Share:{' '}
                        <Link
                          href={candidate.share_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {candidate.share_url}
                        </Link>
                      </Typography>
                    )}
                    {candidate.demo_user_id && (
                      <Typography variant="body2" color="text.secondary">
                        Demo user id: {candidate.demo_user_id}
                      </Typography>
                    )}
                  </Stack>
                </>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 2 }}
              >
                Candidate #{candidate.id} · set_tracks {candidate.set_tracks_id}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Outreach tracking
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="demo-status-label">Status</InputLabel>
                  <Select
                    labelId="demo-status-label"
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Outreach notes"
                  multiline
                  minRows={4}
                  value={outreachNotes}
                  onChange={(e) => setOutreachNotes(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Last outreach"
                  type="datetime-local"
                  value={lastOutreachDt}
                  onChange={(e) => setLastOutreachDt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />

                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Save
                </Button>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Demo account
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleCreateDemo(true)}
                  disabled={creating}
                >
                  Dry run
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => handleCreateDemo(false)}
                  disabled={creating}
                >
                  Create / refresh demo
                </Button>
                {candidate.demo_user_id && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleCleanup}
                    disabled={cleaning}
                  >
                    Cleanup demo user
                  </Button>
                )}
              </Stack>

              {demoResult && (
                <Box
                  sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Latest demo run
                    {demoResult.dry_run ? ' (dry run)' : ''}
                  </Typography>
                  <Typography variant="body2">
                    Login: {demoResult.login}
                  </Typography>
                  <Typography variant="body2">
                    Password: {demoResult.password}
                  </Typography>
                </Box>
              )}
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Outreach message
              </Typography>
              <TextField
                multiline
                minRows={10}
                value={outreachMessage}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ mb: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyMessage}
              >
                Copy message
              </Button>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

export default DemoCandidate;
